// Argus — AgentDashboard: header agent + iconițe panel launchers (Faza 3+ deschide ferestre flotante).

import { useState, useEffect } from 'react';
import { Box, Typography, Chip, Card, Tooltip, Avatar, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, CircularProgress } from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DesktopMacIcon from '@mui/icons-material/DesktopMac';
import VideocamIcon from '@mui/icons-material/Videocam';
import SettingsIcon from '@mui/icons-material/Settings';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import LinkIcon from '@mui/icons-material/Link';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BuildIcon from '@mui/icons-material/Build';
import TerminalIcon from '@mui/icons-material/Terminal';
import SpeedIcon from '@mui/icons-material/Speed';
import StorageIcon from '@mui/icons-material/Storage';
import SaveIcon from '@mui/icons-material/Save';
import MonitorIcon from '@mui/icons-material/Monitor';
import SendIcon from '@mui/icons-material/Send';
import { wsClient } from '../../lib/wsClient';
import { useQuery } from '@tanstack/react-query';
import { fetchAgents, type Agent } from '../../lib/api';
import type { PanelType } from '../../lib/layoutPrefs';
import { dayjs } from '../../lib/dayjsSetup';
import { useAgentVersionStatus } from '../../lib/agentVersion';
import { AgentVersionModal } from './AgentVersionModal';
import { VersionLink } from '../shared/VersionLink';

export interface AgentDashboardProps {
  agentId: string;
  onOpenPanel?: (panelType: PanelType, agentId: string) => void;
}

const PANELS: ReadonlyArray<{ type: PanelType; label: string; icon: typeof FolderOpenIcon; color: string }> = [
  { type: 'fileExplorer', label: 'File Explorer', icon: FolderOpenIcon, color: '#3B82F6' },
  { type: 'desktop', label: 'Desktop', icon: DesktopMacIcon, color: '#8B5CF6' },
  { type: 'webcam', label: 'Webcam', icon: VideocamIcon, color: '#EC4899' },
  { type: 'systemInfo', label: 'System Info', icon: MonitorHeartIcon, color: '#10B981' },
  // Bloc webrtc-audio-support 2026-05-11: card Recordings drop (post recording-browser-pivot 2026-05-09
  // recording = browser-side via MediaRecorder; modal Recordings rămăsese fără funcţionalitate utilizabilă).
  { type: 'urlMonitoring', label: 'URL Monitoring', icon: LinkIcon, color: '#0EA5E9' },
  // Bloc task-manager-services 2026-05-07
  { type: 'taskManager', label: 'Task Manager', icon: ListAltIcon, color: '#F97316' },
  { type: 'services', label: 'Services', icon: BuildIcon, color: '#14B8A6' },
  // Bloc remote-powershell-shell 2026-05-08
  { type: 'powerShell', label: 'PowerShell', icon: TerminalIcon, color: '#1F2937' },
  { type: 'settings', label: 'Settings', icon: SettingsIcon, color: '#6B7280' },
];

export function AgentDashboard({ agentId, onOpenPanel }: AgentDashboardProps): JSX.Element {
  const { data: agents } = useQuery({
    queryKey: ['agents'],
    queryFn: fetchAgents,
    refetchInterval: 5_000,
    refetchOnWindowFocus: false,
  });

  const agent: Agent | undefined = agents?.find((a) => a.id === agentId);
  const versionStatus = useAgentVersionStatus(agentId);
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [sendMessageOpen, setSendMessageOpen] = useState(false);

  // Bloc v1.0.9 #2 — 1Hz live metrics override (separat de heartbeat 10s persist DB).
  // Agent trimite agent_metrics_live când e WS connected; server filtrează pe permission;
  // wsClient dispatch CustomEvent argus:agent-metrics-live → setLiveMetrics 1Hz.
  // Render preferă liveMetrics dacă există; fallback la effectiveMetrics!.
  const [liveMetrics, setLiveMetrics] = useState<{ cpuPct: number; ramUsedMb: number; ramTotalMb: number; diskFreeGb: number; diskTotalGb: number } | null>(null);
  // v1.0.34: `version` vine în payload-ul 1Hz când agentul e online — folosit pentru
  // display instant în VersionLink (în loc să așteptăm 10s heartbeat + invalidate REST).
  const [liveVersion, setLiveVersion] = useState<string | null>(null);
  useEffect(() => {
    setLiveMetrics(null);
    setLiveVersion(null);
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail as {
        agentId?: string;
        metrics?: { cpuPct: number; ramUsedMb: number; ramTotalMb: number; diskFreeGb: number; diskTotalGb: number };
        version?: string | null;
      };
      if (!detail || detail.agentId !== agentId) return;
      if (detail.metrics) setLiveMetrics(detail.metrics);
      if (typeof detail.version === 'string' && /^\d+\.\d+\.\d+$/.test(detail.version)) {
        setLiveVersion(detail.version);
      }
    };
    window.addEventListener('argus:agent-metrics-live', handler);
    return () => window.removeEventListener('argus:agent-metrics-live', handler);
  }, [agentId]);
  const effectiveMetrics = liveMetrics ?? agent?.lastMetrics;

  if (!agent) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Agent unavailable or access restricted.
        </Typography>
      </Box>
    );
  }

  // Pentru "Online for X" folosim lastConnectedAt (moment connect WS), NU lastHeartbeatAt
  // care e refresh-uit la 30s și ar afișa mereu "a few seconds".
  const lastSeenAt = agent.lastHeartbeatAt;
  const onlineSinceAt = agent.lastConnectedAt ?? lastSeenAt;
  const relativeText = agent.isOnline
    ? onlineSinceAt
      ? `Online for ${dayjs(onlineSinceAt).fromNow(true)}`
      : 'Online'
    : lastSeenAt
      ? `Last seen ${dayjs(lastSeenAt).fromNow()}`
      : 'No data';

  const ramTotal = effectiveMetrics!?.ramTotalMb ?? null;
  const diskTotal = effectiveMetrics!?.diskTotalGb ?? null;

  return (
    <Box sx={{ p: 3, height: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, mb: 1 }}>
          <Avatar sx={{ bgcolor: agent.isOnline ? 'success.main' : 'error.main', width: 48, height: 48 }}>
            <DesktopMacIcon />
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }} noWrap>
                {agent.pcName || agent.hostname}
              </Typography>
              {versionStatus.data && (
                <VersionLink
                  status={
                    agent.isOnline && liveVersion
                      ? { ...versionStatus.data, currentVersion: liveVersion }
                      : versionStatus.data
                  }
                  onClick={() => setVersionModalOpen(true)}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                {agent.windowsUsername || agent.lastSeenUser || '(no user)'}
              </Typography>
              <Chip
                size="small"
                label={agent.isOnline ? 'Online' : 'Offline'}
                color={agent.isOnline ? 'success' : 'default'}
                sx={{ height: 20 }}
              />
              <Typography variant="caption" color="text.secondary">
                · {relativeText}
              </Typography>
            </Box>
          </Box>

          {effectiveMetrics! ? (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'stretch', minWidth: 480, flexShrink: 0 }}>
              <MetricCard
                icon={<SpeedIcon sx={{ fontSize: 28 }} />}
                value={`${Math.round(effectiveMetrics!.cpuPct)}%`}
                color="#3B82F6"
                tooltip={`CPU usage: ${Math.round(effectiveMetrics!.cpuPct)}%`}
              />
              <MetricCard
                icon={<StorageIcon sx={{ fontSize: 28 }} />}
                value={ramTotal
                  ? `${((ramTotal - effectiveMetrics!.ramUsedMb) / 1024).toFixed(1)} GB`
                  : `${(effectiveMetrics!.ramUsedMb / 1024).toFixed(1)} GB`}
                color="#A855F7"
                tooltip={ramTotal
                  ? `RAM free: ${((ramTotal - effectiveMetrics!.ramUsedMb) / 1024).toFixed(1)} GB / ${(ramTotal / 1024).toFixed(1)} GB`
                  : `RAM used: ${(effectiveMetrics!.ramUsedMb / 1024).toFixed(1)} GB`}
              />
              <MetricCard
                icon={<SaveIcon sx={{ fontSize: 28 }} />}
                value={`${effectiveMetrics!.diskFreeGb} GB`}
                color="#10B981"
                tooltip={diskTotal
                  ? `Disk free: ${effectiveMetrics!.diskFreeGb} GB / ${diskTotal} GB`
                  : `Disk free: ${effectiveMetrics!.diskFreeGb} GB`}
              />
              <MetricCard
                icon={<MonitorIcon sx={{ fontSize: 28 }} />}
                value={`${agent.monitors?.length ?? 0}`}
                color="#F59E0B"
                tooltip={`${agent.monitors?.length ?? 0} monitor(s)`}
              />
            </Box>
          ) : null}
        </Box>
      </Box>

      <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        Available panels
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1.5 }}>
        {PANELS.map((panel) => {
          const Icon = panel.icon;
          return (
            <Tooltip key={panel.type} title={`Open ${panel.label}`}>
              <Card
                sx={{
                  width: 140,
                  height: 100,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 150ms',
                  '&:hover': {
                    borderColor: panel.color,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${panel.color}33`,
                  },
                }}
                onClick={() => {
                  onOpenPanel?.(panel.type, agentId);
                }}
              >
                <Icon sx={{ fontSize: 32, color: panel.color, mb: 0.5 }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {panel.label}
                </Typography>
              </Card>
            </Tooltip>
          );
        })}
      </Box>

      <Box sx={{ mt: 4, p: 2, backgroundColor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          Click a panel to open a floating window.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SendIcon />}
          onClick={() => setSendMessageOpen(true)}
          disabled={!agent.isOnline}
        >
          Send message
        </Button>
      </Box>

      <SendMessageDialog
        agentId={agentId}
        agentName={agent.pcName || agent.hostname}
        open={sendMessageOpen}
        onClose={() => setSendMessageOpen(false)}
      />

      <AgentVersionModal
        agentId={agentId}
        pcName={agent.pcName || agent.hostname}
        open={versionModalOpen}
        onClose={() => setVersionModalOpen(false)}
      />
    </Box>
  );
}

function SendMessageDialog({ agentId, agentName, open, onClose }: {
  agentId: string; agentName: string; open: boolean; onClose: () => void;
}): JSX.Element {
  const [title, setTitle] = useState('System admin message');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSend = async () => {
    if (!body.trim()) return;
    setSending(true);
    setError(null);
    setSuccess(false);
    try {
      await wsClient.requestSendMessage(agentId, body, title || 'System admin message', `Send message to ${agentName}`);
      setSuccess(true);
      setBody('');
      setTimeout(() => { setSuccess(false); onClose(); }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send message → {agentName}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>Mesaj livrat — utilizator a apăsat OK.</Alert>}
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          margin="dense"
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          label="Body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          multiline
          minRows={4}
          maxRows={10}
          fullWidth
          margin="dense"
          slotProps={{ htmlInput: { maxLength: 2000 } }}
          autoFocus
          placeholder="Mesajul va apărea ca popup pe desktop-ul utilizatorului. Va trebui să apese OK pentru a-l închide."
        />
        <Typography variant="caption" color="text.secondary">
          Sub-secundă livrare via WS. Promise rezolvă DUPĂ click OK al utilizatorului (timeout 10 min).
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void handleSend()}
          disabled={sending || !body.trim()}
          startIcon={sending ? <CircularProgress size={14} /> : <SendIcon />}
        >
          {sending ? 'Trimit...' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MetricCard({ icon, value, color, tooltip }: { icon: JSX.Element; value: string; color: string; tooltip?: string }): JSX.Element {
  const card = (
    <Card
      sx={{
        px: 1.5,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 1.25,
        flex: '1 1 0',
        minWidth: 0,
        minHeight: 72,
        borderLeft: '3px solid',
        borderLeftColor: color,
      }}
    >
      <Box sx={{ color, display: 'flex', flexShrink: 0 }}>{icon}</Box>
      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18 }} noWrap>
        {value}
      </Typography>
    </Card>
  );
  return tooltip ? <Tooltip title={tooltip}>{card}</Tooltip> : card;
}
